import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { kycService } from '../../services/loanService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const EMPLOYMENT_STATUSES = [
  ['employed', 'Employed'], ['self_employed', 'Self Employed'],
  ['unemployed', 'Unemployed'], ['student', 'Student'], ['retired', 'Retired'],
];

const TIER_META = {
  basic:    { label: 'Basic',    color: '#94a3b8' },
  standard: { label: 'Standard', color: '#60a5fa' },
  gold:     { label: 'Gold',     color: '#F5A623' },
  premium:  { label: 'Premium',  color: '#818CF8' },
};

const KYC_STATUS_META = {
  not_submitted: { label: 'Not Submitted', bg: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
  pending:       { label: 'Pending Review', bg: 'rgba(91,142,255,0.08)', color: '#5B8EFF', border: 'rgba(91,142,255,0.25)' },
  under_review:  { label: 'Under Review',  bg: 'rgba(91,142,255,0.08)', color: '#5B8EFF', border: 'rgba(91,142,255,0.25)' },
  approved:      { label: 'Approved',      bg: 'rgba(0,200,150,0.08)',  color: '#00C896', border: 'rgba(0,200,150,0.25)' },
  rejected:      { label: 'Rejected',      bg: 'rgba(255,71,87,0.08)', color: '#FF4757', border: 'rgba(255,71,87,0.25)' },
  resubmit:      { label: 'Resubmit',      bg: 'rgba(255,153,0,0.08)', color: '#ff9900', border: 'rgba(255,153,0,0.25)' },
};

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '0.75rem' }}>
      {children}
    </div>
  );
}

function FileField({ label, name, hint }) {
  const [fileName, setFileName] = useState('');
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <label style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.625rem 0.875rem',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${fileName ? 'rgba(0,200,150,0.4)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        cursor: 'pointer',
        transition: 'border-color .15s',
      }}>
        <span style={{ fontSize: '1rem' }}>📎</span>
        <span style={{ fontSize: '0.8rem', color: fileName ? 'var(--text-1)' : 'var(--text-3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fileName || hint}
        </span>
        <input
          type="file" name={name}
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={e => setFileName(e.target.files?.[0]?.name || '')}
        />
      </label>
    </div>
  );
}

// ── KYC Verification Progress Dialog ──────────────────────────────────────────

function KycProgressDialog({ phase, errorMsg, onClose }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const startTimeRef = useRef(null);

  const colors = { scanning: '#00C896', approved: '#00C896', failed: '#FF4757' };
  const color = colors[phase] || '#00C896';

  // Rotating arc animation for scanning phase
  useEffect(() => {
    if (phase !== 'scanning') { cancelAnimationFrame(animFrameRef.current); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const cx = size / 2, cy = size / 2, r = size / 2 - 4;

    const draw = (ts) => {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      ctx.clearRect(0, 0, size, size);

      // Background ring
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0,200,150,0.12)';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Spinning arc
      const angle = (elapsed / 1000) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, angle, angle + Math.PI * 1.2);
      ctx.strokeStyle = '#00C896';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();

      animFrameRef.current = requestAnimationFrame(draw);
    };
    animFrameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [phase]);

  const titles = { scanning: 'Verifying Identity', approved: 'Identity Verified!', failed: 'Verification Failed' };
  const subtitles = {
    scanning: 'Checking BVN & NIN records…',
    approved: 'You are fully verified and can now apply for loans.',
    failed:   errorMsg || 'Please check your BVN, NIN, and date of birth then try again.',
  };
  const icons = { approved: '✓', failed: '✕' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: '1.25rem', padding: '2.5rem 2rem',
        maxWidth: 360, width: '90%', textAlign: 'center',
        boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
        animation: 'fadeInScale 0.25s ease',
      }}>
        {/* Icon */}
        <div style={{ position: 'relative', width: 88, height: 88, margin: '0 auto 1.5rem' }}>
          {phase === 'scanning' ? (
            <>
              <canvas ref={canvasRef} width={88} height={88} style={{ position: 'absolute', inset: 0 }} />
              <div style={{
                position: 'absolute', inset: 10,
                background: 'rgba(0,200,150,0.08)', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '2rem',
                animation: 'pulse 1.2s ease-in-out infinite',
              }}>🔍</div>
            </>
          ) : (
            <div style={{
              width: 88, height: 88, borderRadius: '50%',
              background: `${color}18`, border: `2px solid ${color}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2.2rem', color,
              animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
              {icons[phase]}
            </div>
          )}
        </div>

        <p style={{ fontWeight: 700, fontSize: '1.05rem', color, marginBottom: '0.5rem' }}>
          {titles[phase]}
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', lineHeight: 1.6 }}>
          {subtitles[phase]}
        </p>

        {phase === 'scanning' && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginTop: '1.25rem' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#00C896',
                animation: `dotBounce 0.9s ${i * 0.3}s ease-in-out infinite`,
              }} />
            ))}
          </div>
        )}

        {(phase === 'approved' || phase === 'failed') && (
          <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '1.5rem', width: '100%' }}>
            {phase === 'approved' ? 'Continue' : 'Dismiss'}
          </button>
        )}
      </div>

      <style>{`
        @keyframes fadeInScale { from { opacity:0; transform:scale(0.9); } to { opacity:1; transform:scale(1); } }
        @keyframes popIn { from { opacity:0; transform:scale(0.5); } to { opacity:1; transform:scale(1); } }
        @keyframes pulse { 0%,100% { transform:scale(0.95); } 50% { transform:scale(1.05); } }
        @keyframes dotBounce { 0%,100% { transform:translateY(0); opacity:0.3; } 50% { transform:translateY(-6px); opacity:1; } }
      `}</style>
    </div>
  );
}

// ── Main Profile page ──────────────────────────────────────────────────────────

export default function Profile() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [kycForm, setKycForm] = useState({
    bvn: '', nin: '', date_of_birth: '', gender: '',
    employment_status: 'employed', employer_name: '', monthly_income: '',
    bank_name: '', bank_code: '', account_number: '',
  });
  const [kycPhase, setKycPhase] = useState(null); // null | 'scanning' | 'approved' | 'failed'
  const [kycError, setKycError] = useState('');

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile().then(r => r.data.data),
  });
  const { data: kycData } = useQuery({
    queryKey: ['kyc-status'],
    queryFn: () => kycService.getStatus().then(r => r.data.data),
  });

  // Pre-fill DOB from profile
  useEffect(() => {
    if (profileData?.date_of_birth && !kycForm.date_of_birth) {
      setKycForm(f => ({ ...f, date_of_birth: profileData.date_of_birth }));
    }
    if (profileData?.gender && !kycForm.gender) {
      setKycForm(f => ({ ...f, gender: profileData.gender }));
    }
  }, [profileData]);

  const updateMutation = useMutation({
    mutationFn: d => authService.updateProfile(d),
    onSuccess: () => { setMsg('Profile updated.'); setErr(''); qc.invalidateQueries({ queryKey: ['profile'] }); },
    onError: e => { setErr(e.response?.data?.message || 'Update failed.'); setMsg(''); },
  });

  const kycMutation = useMutation({
    mutationFn: d => kycService.submit(d),
    onSuccess: (res) => {
      const autoApproved = res.data?.auto_approved;
      if (autoApproved) {
        qc.invalidateQueries({ queryKey: ['profile'] });
        setKycPhase('approved');
      } else {
        setKycError('We could not verify your identity. Please check your BVN, NIN, and date of birth.');
        setKycPhase('failed');
      }
    },
    onError: e => {
      setKycError(e.response?.data?.message || 'KYC submission failed.');
      setKycPhase('failed');
    },
  });

  if (isLoading) return <LoadingSpinner />;
  const profile = profileData || {};
  const kyc = kycData || {};
  const kycStatus = kyc.status || 'not_submitted';
  const isKycVerified = profile.is_kyc_verified === true;
  const completion = profile.profile_completion ?? null;
  const statusMeta = KYC_STATUS_META[kycStatus] || KYC_STATUS_META.not_submitted;
  const tierMeta = TIER_META[profile.tier || 'basic'];
  const canSubmitKyc = !isKycVerified;

  const handleProfileSubmit = e => {
    e.preventDefault();
    const fd = new FormData(e.target);
    updateMutation.mutate(Object.fromEntries(fd));
  };

  const handleKycSubmit = e => {
    e.preventDefault();
    setKycError('');
    setKycPhase('scanning');
    const fd = new FormData(e.target);
    Object.entries(kycForm).forEach(([k, v]) => { if (!fd.has(k)) fd.append(k, v); });
    kycMutation.mutate(fd);
  };

  const handleDialogClose = () => {
    if (kycPhase === 'approved') setMsg('✓ Identity verified! You can now apply for loans.');
    setKycPhase(null);
  };

  return (
    <div>
      {kycPhase && <KycProgressDialog phase={kycPhase} errorMsg={kycError} onClose={handleDialogClose} />}

      <div className="page-header">
        <h1>Profile & KYC</h1>
        <p>Manage your personal information and identity verification</p>
      </div>

      {msg && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <span>✓</span><span>{msg}</span>
        </div>
      )}
      {err && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          <span>⚠</span><span>{err}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', alignItems: 'start' }}>

        {/* ── Personal Information ── */}
        <div className="card" style={{ gridColumn: '1 / 2' }}>
          {completion !== null && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
                <span style={{ color: 'var(--text-3)' }}>Profile Completion</span>
                <span style={{ fontWeight: 700, color: completion >= 80 ? '#00C896' : completion >= 50 ? '#F5A623' : '#FF4757' }}>{completion}%</span>
              </div>
              <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${completion}%`, borderRadius: 99, background: completion >= 80 ? '#00C896' : completion >= 50 ? '#F5A623' : '#FF4757', transition: 'width 0.4s' }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <SectionLabel>Personal Information</SectionLabel>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              {isKycVerified && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
                  borderRadius: '99px', letterSpacing: '0.05em', textTransform: 'uppercase',
                  background: 'rgba(0,200,150,0.12)', color: '#00C896', border: '1px solid rgba(0,200,150,0.3)',
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                }}>
                  <span>✓</span> Verified
                </span>
              )}
              {tierMeta && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                  padding: '0.2rem 0.6rem', borderRadius: '99px',
                  background: `${tierMeta.color}18`, color: tierMeta.color,
                  border: `1px solid ${tierMeta.color}40`, textTransform: 'uppercase',
                }}>
                  {tierMeta.label} Tier
                </span>
              )}
            </div>
          </div>

          <form onSubmit={handleProfileSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" name="first_name" defaultValue={profile.first_name} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" name="last_name" defaultValue={profile.last_name} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={profile.email || ''} readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="form-input" name="phone_number" defaultValue={profile.phone_number} placeholder="+234..." />
            </div>
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input className="form-input" type="date" name="date_of_birth" defaultValue={profile.date_of_birth} />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-input" name="gender" defaultValue={profile.gender || ''}>
                <option value="" disabled>Select…</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea className="form-input" name="address" rows={2} defaultValue={profile.address} placeholder="Your home address" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-input" name="state" defaultValue={profile.state} />
              </div>
              <div className="form-group">
                <label className="form-label">Country</label>
                <input className="form-input" name="country" defaultValue={profile.country} />
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              <SectionLabel>Next of Kin</SectionLabel>
            </div>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" name="next_of_kin_name" defaultValue={profile.next_of_kin_name} placeholder="Next of kin full name" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-input" name="next_of_kin_phone" defaultValue={profile.next_of_kin_phone} placeholder="+234..." />
              </div>
              <div className="form-group">
                <label className="form-label">Relationship</label>
                <select className="form-input" name="next_of_kin_relationship" defaultValue={profile.next_of_kin_relationship || ''}>
                  <option value="">— Select —</option>
                  {['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'].map(r => (
                    <option key={r} value={r.toLowerCase()}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* ── KYC Verification ── */}
        <div className="card" style={{ gridColumn: '2 / 3' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <SectionLabel>KYC Verification</SectionLabel>
            <span style={{
              fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
              borderRadius: '99px', letterSpacing: '0.05em', textTransform: 'uppercase',
              background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.border}`,
            }}>
              {statusMeta.label}
            </span>
          </div>

          {isKycVerified ? (
            <div style={{
              background: 'rgba(0,200,150,0.06)', border: '1px solid rgba(0,200,150,0.2)',
              borderRadius: 'var(--radius-lg)', padding: '1.5rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✓</div>
              <p style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.25rem' }}>Identity Verified</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>You are eligible to apply for loans.</p>
            </div>
          ) : (
            <>
              {kyc.rejection_reason && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  <span>⚠</span>
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.2rem' }}>Rejection Reason</strong>
                    <span style={{ fontSize: '0.875rem' }}>{kyc.rejection_reason}</span>
                  </div>
                </div>
              )}

              {canSubmitKyc && (
                <form onSubmit={handleKycSubmit}>
                  <SectionLabel>Identity Numbers</SectionLabel>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">BVN</label>
                      <input className="form-input" name="bvn" maxLength={11} placeholder="11 digits"
                        value={kycForm.bvn} onChange={e => setKycForm({ ...kycForm, bvn: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">NIN</label>
                      <input className="form-input" name="nin" maxLength={11} placeholder="11 digits"
                        value={kycForm.nin} onChange={e => setKycForm({ ...kycForm, nin: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input className="form-input" type="date" name="date_of_birth"
                      value={kycForm.date_of_birth}
                      onChange={e => setKycForm({ ...kycForm, date_of_birth: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Gender</label>
                    <select className="form-input" name="gender" required value={kycForm.gender}
                      onChange={e => setKycForm({ ...kycForm, gender: e.target.value })}>
                      <option value="" disabled>Select…</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <SectionLabel>Documents</SectionLabel>
                  <div className="grid-2">
                    <FileField label="Selfie" name="selfie" hint="Clear photo of your face" />
                    <FileField label="Proof of Address" name="proof_of_address" hint="Utility bill / bank statement" />
                  </div>

                  <SectionLabel>Employment & Income</SectionLabel>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Employment Status</label>
                      <select className="form-input" name="employment_status" value={kycForm.employment_status}
                        onChange={e => setKycForm({ ...kycForm, employment_status: e.target.value })}>
                        {EMPLOYMENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Employer Name</label>
                      <input className="form-input" name="employer_name" placeholder="Optional"
                        value={kycForm.employer_name} onChange={e => setKycForm({ ...kycForm, employer_name: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Monthly Income (₦)</label>
                    <input className="form-input" type="number" name="monthly_income" placeholder="e.g. 150000"
                      value={kycForm.monthly_income} onChange={e => setKycForm({ ...kycForm, monthly_income: e.target.value })} />
                  </div>

                  <SectionLabel>Bank Details</SectionLabel>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Bank Name</label>
                      <input className="form-input" name="bank_name" placeholder="e.g. GTBank"
                        value={kycForm.bank_name} onChange={e => setKycForm({ ...kycForm, bank_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Bank Code <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span></label>
                      <input className="form-input" name="bank_code" placeholder="e.g. 058"
                        value={kycForm.bank_code} onChange={e => setKycForm({ ...kycForm, bank_code: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input className="form-input" name="account_number" maxLength={10} placeholder="10 digits"
                      value={kycForm.account_number} onChange={e => setKycForm({ ...kycForm, account_number: e.target.value })} />
                  </div>

                  <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
                    disabled={kycMutation.isPending}>
                    {kycStatus === 'rejected' || kycStatus === 'resubmit' ? 'Resubmit KYC' : 'Submit KYC'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
